import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { MAX_DELEGATION_TARGETS, type DelegationOptionsDto, type FillSheetDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { DELEGATE_COPY } from './copy';
import { DelegationRowsEditor, type DelegationRow } from './DelegationRowsEditor';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §1d] DELEGATE MODE — ONE LINE.
 *
 *  A unit picker, a count, an add-another action and a confirm action, all on the row where
 *  the chips were. It replaces `DelegateModal`, which is deleted: a dialog for a two-field
 *  decision put the choice a click away from the row it was about.
 *
 *  ⚠️ THE VERB IS UNCHANGED. `GET /duties/:id/delegation` for the numbers and
 *  `POST /duties/:id/delegations` for the write, both exactly as [C1b] built them. The body
 *  carries the FULL desired split and REPLACES, so a retry after a dropped response is a
 *  no-op rather than a double split.
 *
 *  ⚠️ THE UNIT LIST COMES FROM THAT ENDPOINT, NEVER FROM `/org-units/tree`. The tree is scoped
 *  to the CALLER; this control's subject is the SOURCE ADDRESSEE's subtree, and for a
 *  CP-office nodal those are different sets. It also carries each unit's own named FLOOR,
 *  which a tree cannot. A picker that offers what the write refuses is what
 *  `eligibility.service.ts` forbids one level down.
 *
 *  ⚠️ §1d's "constrained to the caller's own division" IS REFUSED SERVER-SIDE, not merely
 *  absent from the picker — `assertDelegationTargets` walks `ancestors` and answers
 *  "A source can only be split across units inside it". [C1b §3e]'s pair: the caller half is
 *  `requireDelegationContext`'s share test, the target half is that walk. This control is the
 *  convenience.
 *
 *  ─── ⚠️ [amended UX1 §2] IT OPENS ON AN ALREADY-SPLIT ROW NOW, AND THAT MADE THE SEEDING
 *      BELOW MANDATORY RATHER THAN COSMETIC ────────────────────────────────────────────
 *
 *  [C1f §1b] offered this control only on an EMPTY post, so the row set could safely start at
 *  one blank line: there was never a split to preserve. §2 reverses that — delegation is
 *  available whenever the source is `Any` and capacity remains — and the write REPLACES.
 *
 *  ⚠️ SO AN UNSEEDED CONFIRM WOULD HAVE SILENTLY UN-DELEGATED THE EXISTING SPLIT. `targets`
 *  is the FULL desired state; a blank row set posts an empty array, and `planDelegation`'s
 *  per-target loop only refuses a family row dropped below its own `namedFloor` — so a
 *  delegated row carrying ZERO names is dropped with no error, no refusal and nothing on
 *  screen. The wire has always carried `delegatedCount` per option; nothing read it.
 *
 *  ⚠️ AND `entered > 0` HAD TO GO WITH IT. Zeroing every row IS the un-delegation verb
 *  ([C1b §3d]: "a unit at zero, or absent, gets no row"), and with a split stored it is the
 *  only route back to it. It stays forbidden when nothing is stored, where it is a no-op.
 *
 *  ⚠️ AND A FAILED OPTIONS READ SAYS SO. `DelegateModal` never consulted `isError`, so
 *  opening it on an already-delegated row — which the chain guard 400s — left it on
 *  "Loading…" for ever. §1b makes that particular trigger unreachable, which is not a fix:
 *  a 404 from a re-stamped column, a 500 or a dropped connection all still land here.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type DelegateInlineProps = {
  dutyId: string;
  lineKey: string;
  allocKey: string;
  onCancel: () => void;
  onDelegated: (sheet: FillSheetDto) => void;
};

/*
 * ⚠️ [C6a §2d] A THIN CONTAINER NOW — the rows, the seed latch, the layout and the refusal moved to
 * `DelegationRowsEditor`, which a command host shares. What stays HERE is everything that is about
 * FORCE: the endpoint, the query key, the unit picker's options, the pool, the ceiling, the per-row
 * named floor and the sentences that state them. The props, the `delegate-*` test ids,
 * `data-delegate-row` and the behaviour are unchanged, and the test of this extraction is that the
 * suites reaching it pass UNEDITED: `DelegateInline.test.tsx` does; `e2e/specs/fillGaps.spec.ts`
 * must too, and has NOT been run against it yet — a browser claim is not made until it is.
 */
export const DelegateInline = ({ dutyId, lineKey, allocKey, onCancel, onDelegated }: DelegateInlineProps) => {
  const options = useQuery({
    queryKey: queryKeys.fill.delegation(dutyId, lineKey, allocKey),
    queryFn: () => api.get<DelegationOptionsDto>(`/duties/${dutyId}/delegation`, { query: { lineKey, allocKey } }),
  });

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [UX1 §2] THE SPLIT THIS ROW ALREADY HOLDS, SEEDED FROM THE SERVER'S ANSWER.
   *
   *  See the header: without this, confirming an unchanged form un-delegates everything. The
   *  editor latches it on a fingerprint of what it IS, never on a bare effect ([P3b.3]).
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const held = options.data?.options.filter((option) => option.delegatedCount > 0) ?? [];
  const seed = options.data
    ? held.map((option) => ({ target: option.orgUnit.id, count: option.delegatedCount }))
    : undefined;

  const view = options.data;

  /* ⚠️ `entered` is the FORCE split's sum — the one number this host exists to bound. */
  const enteredOf = (rows: readonly DelegationRow[]): number => rows.reduce((running, row) => running + row.count, 0);

  /*
   * ⚠️ [UX1 §2] THE SERVER'S OWN PER-TARGET RULE, MIRRORED SO THE FORM CANNOT OFFER WHAT THE
   * WRITE REFUSES — `planDelegation`'s floor loop. `eligibility.service.ts`'s rule one level
   * down: the set OFFERED and the set ACCEPTED must not be able to disagree.
   */
  const floorOf = (unitOrgUnitId: string | null): number =>
    (unitOrgUnitId ? view?.options.find((option) => option.orgUnit.id === unitOrgUnitId)?.namedFloor : 0) ?? 0;

  /*
   * ⚠️ §1d: ADD-ANOTHER IS UNAVAILABLE ONCE THE ENTERED COUNTS REACH THE REQUIREMENT. Three
   * more conditions are needed and are not in the brief: an unfinished last row would collect
   * blanks, the unit list is finite, and `MAX_DELEGATION_TARGETS` bounds the wire.
   *
   * ⚠️ [amended OTHERS-EXPAND] THAT LAST ONE IS A TERM NOW, NOT A SENTENCE. The comment used to
   * lean on "the unit list is finite" to cover it, which held only while a division's delegable
   * units never exceeded sixty. Section expansion put about a thousand `SECTION` units beneath
   * the divisions, so `view.options` can be longer than `delegateAllocationSchema` will accept
   * — and a sixty-first row would be offered, filled in, and refused as a whole split with a
   * 422 naming an array length the operator never saw. The form stops where the write stops.
   */
  const canAddUnit = (rows: readonly DelegationRow[]): boolean => {
    if (!view) return false;
    const chosen = rows.flatMap((row) => (row.target ? [row.target] : []));
    const lastRow = rows[rows.length - 1];
    return (
      enteredOf(rows) < view.pool &&
      lastRow !== undefined &&
      lastRow.target !== null &&
      lastRow.count > 0 &&
      chosen.length < view.options.length &&
      chosen.length < MAX_DELEGATION_TARGETS
    );
  };

  /*
   * ⚠️ BOUNDED BY `delegable`, NEVER BY `pool` — and §1b does NOT make that dead. `delegable`
   * is `pool − maxNamed(source)` where the max is taken across EVERY live night of this duty
   * point, so a post empty tonight can be full on Tuesday. The ceiling sentence below is the
   * only place an operator learns which night is binding.
   *
   * ⚠️ `entered > 0 || heldSplit` — see the header. With nothing stored, confirming zero is a
   * no-op and stays refused; with a split stored, confirming zero IS how the division takes
   * the post back, and it is the only route to it.
   */
  const canConfirm = (rows: readonly DelegationRow[]): boolean => {
    if (!view) return false;
    const entered = enteredOf(rows);
    const overCeiling = entered > view.delegable;
    const belowFloor = rows.some((row) => row.target !== null && row.count < floorOf(row.target));
    const heldSplit = held.length > 0;
    return (entered > 0 || heldSplit) && !overCeiling && !belowFloor;
  };

  const submit = async (rows: readonly DelegationRow[]): Promise<void> => {
    const sheet = await api.post<FillSheetDto>(`/duties/${dutyId}/delegations`, {
      lineKey,
      allocKey,
      targets: rows.flatMap((row) => (row.target ? [{ unitOrgUnitId: row.target, count: row.count }] : [])),
    });
    onDelegated(sheet);
  };

  return (
    <DelegationRowsEditor
      /* ⚠️ `isError` BEFORE `isPending`, or a refusal renders as a spinner for ever. */
      error={options.isError ? options.error.message : null}
      loading={options.isPending}
      loadingText={DELEGATE_COPY.loading}
      seed={seed}
      empty={view && view.options.length === 0 ? DELEGATE_COPY.noUnits : undefined}
      renderTarget={({ index, value, disabledKeys, onChange }) => (
        <Select
          size="small"
          showSearch
          /* ⚠️ `label`, NEVER the default — rc-select otherwise filters against the VALUE,
             which here is an ObjectId. `RankPicker` passes it for the same reason. */
          optionFilterProp="label"
          className="w-44"
          placeholder={DELEGATE_COPY.pickUnit}
          aria-label={`${DELEGATE_COPY.unitLabel} ${String(index + 1)}`}
          data-testid={`delegate-unit-${String(index)}`}
          value={value}
          options={(view?.options ?? []).map((option) => ({
            value: option.orgUnit.id,
            label: option.orgUnit.name,
            /* Already taken on another row — `delegateAllocationSchema` refuses a repeat. */
            disabled: disabledKeys.has(option.orgUnit.id),
          }))}
          onChange={(next: string) => {
            onChange(next);
          }}
        />
      )}
      minFor={floorOf}
      max={view?.delegable ?? 0}
      canAddRow={canAddUnit}
      canConfirm={canConfirm}
      onConfirm={submit}
      onCancel={onCancel}
      testIdPrefix="delegate"
      rowAttribute="data-delegate-row"
      copy={{
        confirm: DELEGATE_COPY.confirm,
        cancel: DELEGATE_COPY.cancel,
        addRow: DELEGATE_COPY.addUnit,
        /* The server's own sentence, which names the number AND the night. Never a code. */
        refused: DELEGATE_COPY.refused,
        countLabel: (row, index) =>
          DELEGATE_COPY.countFor(
            view?.options.find((option) => option.orgUnit.id === row.target)?.orgUnit.name ?? String(index + 1),
          ),
      }}
      /* ⚠️ [UX1 §2] `DELEGATE_COPY.floor` — the affordance it describes is the per-row minimum above. */
      rowCaption={(row, index) =>
        floorOf(row.target) > 0 ? (
          <span className="text-[11px] text-muted-foreground" data-testid={`delegate-floor-${String(index)}`}>
            {DELEGATE_COPY.floor(floorOf(row.target))}
          </span>
        ) : null
      }
      footer={
        view ? (
          <>
            {/*
              ──────────────────────────────────────────────────────────────────────
               ⚠️ [C1h §3e] THE CAPTION WAS TWO SENTENCES AND ONLY ONE WAS THE DUPLICATE.

               `Delegated 0 · Retained 4 · Total 4` restated the header count — three numbers, of
               which the third is the post header's own and the first two are the two inputs
               already visible in the fields above. It is GONE.

               ⚠️ `This applies to all N duties of this duty point.` STAYS, and it is the answer to
               the question §3e asks before removing anything: **a delegation applies to the WHOLE
               DUTY POINT, every live night of it — never to one night.** `delegateAllocation`
               writes to the ARRANGEMENT's `duty_points[].requirement_lines[].allocations` and
               re-stamps every non-detached deployment of that card; the `:id` in the route is a
               deployment, but it only LOCATES the card. So the sentence is said BEFORE the click,
               and `deploymentCount` is on the wire for no other purpose.

               ⚠️ THE INVARIANT THE DELETED HALF ANNOUNCED IS UNTOUCHED. `retained + Σ delegated =
               pool` is enforced by `delegationPlan.util.ts` and asserted by `delegation.test.ts`
               CLAIM 1 on EVERY night of a three-night fixture. It was never this sentence holding it.
              ──────────────────────────────────────────────────────────────────────
            */}
            <p className="text-xs text-muted-foreground" data-testid="delegate-reach">
              {DELEGATE_COPY.reach(view.deploymentCount)}
            </p>

            {view.sourceNamed > 0 ? (
              <p className="text-xs text-warning" data-testid="delegate-ceiling">
                {DELEGATE_COPY.ceiling(view.sourceNamed, view.bindingDutyDate, view.delegable, view.pool)}
              </p>
            ) : null}
          </>
        ) : null
      }
    />
  );
};
